// LightSwitcher
// Features:
// - Interactive light switch that toggles room lights
// - animated switch rotation based on state
// - click sound effect

import { useEffect, useRef, useMemo } from 'react'
import * as THREE from 'three'
import { RigidBody } from '@react-three/rapier'
import gsap from 'gsap'

import { useStore } from '../../store/useStore'

export default function LightSwitcher({ nodes, material, physicsMaterial }) {
    const meshRef = useRef()
    const switchAudioRef = useRef()

    const isSoundOn = useStore((state) => state.isSoundOn)
    const lightsOn = useStore((state) => state.lightsOn)

    // Physics visual helpers
    const geometry = useMemo(() => new THREE.BoxGeometry(0.35, 0.35, 0.35), [])

    // Interaction: Toggle lights
    useEffect(() => {
        // Setup audio
        const audio = new Audio('/sounds/switch.mp3')
        audio.preload = 'auto'
        audio.volume = isSoundOn ? 0.25 : 0
        switchAudioRef.current = audio

        const onMouseDown = (e) => {
            if (e.button !== 0) return
            const state = useStore.getState()

            if (
                state.currentHitName === 'Light Switch' &&
                state.isPointerLocked
            ) {
                state.toggleLights()

                if (switchAudioRef.current && useStore.getState().isSoundOn) {
                    switchAudioRef.current.currentTime = 0
                    switchAudioRef.current.play().catch(() => {})
                }
            }
        }

        window.addEventListener('mousedown', onMouseDown)
        return () => {
            window.removeEventListener('mousedown', onMouseDown)
            if (switchAudioRef.current) {
                switchAudioRef.current.pause()
                switchAudioRef.current = null
            }
        }
    }, [])

    // Update volume when global setting changes
    useEffect(() => {
        if (switchAudioRef.current) {
            switchAudioRef.current.volume = isSoundOn ? 0.25 : 0
        }
    }, [isSoundOn])

    // Animation: Rotate switch based on state
    useEffect(() => {
        if (meshRef.current) {
            const targetRotation = lightsOn ? -Math.PI / 4 : Math.PI / 4
            gsap.to(meshRef.current.rotation, {
                z: targetRotation,
                duration: 0.2,
                ease: 'power2.out',
            })
        }
    }, [lightsOn])

    return (
        <group>
            {/* Visual Mesh */}
            <mesh
                ref={meshRef}
                geometry={nodes.room_switcher.geometry}
                material={material}
                position={[2.881, 1.804, -0.011]}
                rotation={[0, 0, lightsOn ? -Math.PI / 4 : Math.PI / 4]}
            />

            {/* Physics Collider */}
            <RigidBody
                position={[2.881, 1.804, -0.011]}
                colliders="cuboid"
                type="fixed"
                userData={{ name: 'Light Switch' }}
            >
                <mesh geometry={geometry} material={physicsMaterial} />
            </RigidBody>
        </group>
    )
}
